/*!
 * CarWrapVinyl.com · Builder C · Education sections behavior (vanilla JS, no dependencies)
 * CWV.edu.printStack · processFlow · filmExplorer · timeline · glossary
 * Every section instance is scoped to its own root (several per page are fine),
 * respects prefers-reduced-motion and html[data-shift="off"], and re-inits in the theme editor.
 */
(function () {
  'use strict';

  var CWV = window.CWV = window.CWV || {};
  if (CWV.edu && CWV.edu.__loaded) { return; }

  var doc = document;
  var html = doc.documentElement;
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var hasIO = 'IntersectionObserver' in window;

  function reduced() { return !!(mqReduce && mqReduce.matches); }
  function shiftOff() { return html.getAttribute('data-shift') === 'off'; }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
  function behavior() { return reduced() ? 'auto' : 'smooth'; }
  function rafThrottle(fn) {
    var queued = false;
    return function () {
      if (queued) { return; }
      queued = true;
      window.requestAnimationFrame(function () { queued = false; fn(); });
    };
  }
  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts || false);
    return function () { target.removeEventListener(type, fn, opts || false); };
  }
  function norm(s) {
    s = String(s || '').toLowerCase();
    if (s.normalize) { s = s.normalize('NFD').replace(/[̀-ͯ]/g, ''); }
    return s.replace(/\s+/g, ' ').trim();
  }

  /* ================================================================ PRINT STACK */
  function printStack(el) {
    var stage = el.querySelector('[data-ps-stage]');
    if (!stage) { return null; }
    var slabs = $$('[data-ps-slab]', el);
    var tabs = $$('[data-ps-tab]', el);
    var panels = $$('[data-ps-panel]', el);
    var viewBtns = $$('[data-ps-view]', el);
    var cap = el.querySelector('[data-ps-cap]');
    var offs = [];
    var observers = [];
    var active = 0;
    var userChoseView = false;
    var motion = !reduced();
    var inView = false;
    var hovering = false;
    var idleTimer = null;

    function nameOf(i) {
      var n = tabs[i] && tabs[i].querySelector('.ps-tab-name');
      return n ? n.textContent.trim() : '';
    }
    function setView(v) {
      el.setAttribute('data-view', v);
      viewBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-ps-view') === v)); });
    }
    function activate(i, opts) {
      opts = opts || {};
      if (!tabs.length) { return; }
      i = (i + tabs.length) % tabs.length;
      active = i;
      slabs.forEach(function (s, k) {
        s.classList.toggle('is-active', k === i);
        s.classList.toggle('is-above', k < i);
      });
      tabs.forEach(function (t, k) {
        var sel = k === i;
        t.setAttribute('aria-selected', String(sel));
        t.tabIndex = sel ? 0 : -1;
        t.classList.toggle('is-active', sel);
      });
      panels.forEach(function (p, k) {
        if (k === i) {
          p.hidden = false;
          if (motion) { p.classList.remove('is-shown'); void p.offsetWidth; p.classList.add('is-shown'); }
        } else {
          p.hidden = true;
        }
      });
      if (cap) { cap.textContent = 'Selected · L' + (i + 1) + ' · ' + nameOf(i); }
      if (opts.user) { el.classList.add('is-picked'); }
      if (opts.focus && tabs[i]) { tabs[i].focus(); }
      if (opts.reveal && panels[i]) {
        var r = panels[i].getBoundingClientRect();
        if (r.bottom > window.innerHeight || r.top < 0) { panels[i].scrollIntoView({ block: 'nearest', behavior: behavior() }); }
      }
    }

    viewBtns.forEach(function (b) {
      offs.push(on(b, 'click', function () {
        userChoseView = true;
        setView(b.getAttribute('data-ps-view'));
      }));
    });
    tabs.forEach(function (t, i) {
      offs.push(on(t, 'click', function () { activate(i, { user: true }); }));
      offs.push(on(t, 'keydown', function (e) {
        var k = e.key, next = null;
        if (k === 'ArrowDown' || k === 'ArrowRight') { next = active + 1; }
        else if (k === 'ArrowUp' || k === 'ArrowLeft') { next = active - 1; }
        else if (k === 'Home') { next = 0; }
        else if (k === 'End') { next = tabs.length - 1; }
        if (next !== null) { e.preventDefault(); activate(next, { user: true, focus: true }); }
      }));
    });
    slabs.forEach(function (s, i) {
      offs.push(on(s, 'click', function () { activate(i, { user: true, reveal: true }); }));
    });

    /* label spacing: measure each billboard label and make the exploded gap tall enough that
       neighbouring labels never overlap (labels wrap, so heights change with the stage width).
       Vertical spacing per unit of gap is about sin(--ps-rx); 0.76 covers the pointer tilt. */
    var tagTxts = slabs.map(function (s) { return s.querySelector('.ps-tag-txt'); });
    function fitGap() {
      var hs = tagTxts.map(function (t) { return t ? t.offsetHeight : 0; });
      var need = 0;
      for (var k = 0; k < hs.length - 1; k++) {
        if (hs[k] && hs[k + 1]) { need = Math.max(need, (hs[k] + hs[k + 1]) / 2 + 8); }
      }
      if (need) { el.style.setProperty('--ps-gap-fit', Math.ceil(need / 0.76) + 'px'); }
      else { el.style.removeProperty('--ps-gap-fit'); }
    }
    fitGap();
    if ('ResizeObserver' in window) {
      var fitRO = new ResizeObserver(rafThrottle(fitGap));
      fitRO.observe(stage);
      observers.push(fitRO);
    } else {
      offs.push(on(window, 'resize', rafThrottle(fitGap)));
    }
    if (doc.fonts && doc.fonts.ready) { doc.fonts.ready.then(fitGap); }

    /* first view: compressed -> exploded with a scan sweep (skipped in a background tab,
       where IntersectionObserver may never report, so the stack opens exploded) */
    if (motion && hasIO && !doc.hidden) {
      el.classList.add('no-anim');
      setView('compressed');
      void el.offsetWidth;
      window.requestAnimationFrame(function () { el.classList.remove('no-anim'); });
      var revealIO = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) { return; }
        revealIO.disconnect();
        setTimeout(function () {
          if (!userChoseView) { setView('exploded'); }
          stage.classList.add('is-scanning');
          setTimeout(function () { stage.classList.remove('is-scanning'); }, 1700);
        }, 220);
      }, { threshold: 0.35 });
      revealIO.observe(stage);
      observers.push(revealIO);
    }

    /* idle swing (needs a registered <angle> custom property) + pointer tilt */
    var canSpin = motion && !shiftOff() && window.CSS && typeof window.CSS.registerProperty === 'function';
    if (canSpin) {
      try { window.CSS.registerProperty({ name: '--ps-rz', syntax: '<angle>', inherits: true, initialValue: '45deg' }); }
      catch (e) { /* already registered by another instance */ }
    }
    function idle(onOff) {
      if (!canSpin) { return; }
      stage.classList.toggle('is-idle', !!onOff && inView && !hovering);
    }
    if (hasIO) {
      var visIO = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        idle(inView);
      }, { threshold: 0 });
      visIO.observe(stage);
      observers.push(visIO);
    }
    var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
    if (motion && fine) {
      var px = 0, py = 0;
      var applyTilt = rafThrottle(function () {
        stage.style.setProperty('--ps-rz', (45 + px * 10).toFixed(2) + 'deg');
        stage.style.setProperty('--ps-rx', (58 - py * 7).toFixed(2) + 'deg');
      });
      offs.push(on(stage, 'pointermove', function (e) {
        if (e.pointerType !== 'mouse') { return; }
        var r = stage.getBoundingClientRect();
        px = clamp((e.clientX - r.left) / r.width * 2 - 1, -1, 1);
        py = clamp((e.clientY - r.top) / r.height * 2 - 1, -1, 1);
        hovering = true;
        clearTimeout(idleTimer);
        stage.classList.remove('is-idle');
        applyTilt();
      }));
      offs.push(on(stage, 'pointerleave', function () {
        hovering = false;
        stage.style.removeProperty('--ps-rz');
        stage.style.removeProperty('--ps-rx');
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () { idle(true); }, 900);
      }));
    }

    activate(0);
    el.classList.add('is-ready');

    return {
      el: el,
      select: function (blockEl) {
        var li = blockEl && blockEl.closest ? blockEl.closest('li') : null;
        var items = $$('.ps-list > li', el);
        var i = items.indexOf(li);
        if (i > -1) { activate(i, { user: true }); setView('exploded'); }
      },
      destroy: function () {
        offs.forEach(function (f) { f(); });
        observers.forEach(function (o) { o.disconnect(); });
        clearTimeout(idleTimer);
      }
    };
  }

  /* ================================================================ PROCESS FLOW */
  function processFlow(el) {
    var vp = el.querySelector('[data-pf-viewport]');
    var inner = el.querySelector('[data-pf-inner]');
    if (!vp || !inner) { return null; }
    var steps = $$('[data-pf-step]', el);
    if (!steps.length) { return null; }
    var nav = el.querySelector('[data-pf-nav]');
    var prev = el.querySelector('[data-pf-prev]');
    var next = el.querySelector('[data-pf-next]');
    var curEl = el.querySelector('[data-pf-current]');
    var mini = el.querySelector('[data-pf-mini]');
    var mqV = window.matchMedia ? window.matchMedia('(max-width: 759px)') : { matches: false };
    var offs = [], observers = [];
    var pos = [], start = 0, len = 0, vertical = false, overflow = false, active = false, ro = null;

    function measure() {
      vertical = mqV.matches;
      pos = steps.map(function (s) {
        var n = s.querySelector('.pf-node') || s;
        return vertical ? s.offsetTop + n.offsetTop + n.offsetHeight / 2 : s.offsetLeft + n.offsetLeft + n.offsetWidth / 2;
      });
      start = pos[0] || 0;
      len = Math.max(0, (pos[pos.length - 1] || 0) - start);
      inner.style.setProperty('--rail-start', start + 'px');
      inner.style.setProperty('--rail-len', len + 'px');
      overflow = !vertical && vp.scrollWidth > vp.clientWidth + 2;
      if (nav) { nav.hidden = vertical || !overflow; }
      update();
    }

    function update() {
      var vh = window.innerHeight || 800;
      var r = el.getBoundingClientRect();
      var target;
      if (vertical) {
        var ir = inner.getBoundingClientRect();
        target = vh * 0.62 - ir.top - start;
      } else if (overflow) {
        var max = vp.scrollWidth - vp.clientWidth;
        var probe = vp.scrollLeft + vp.clientWidth * 0.56 - inner.offsetLeft;
        if (vp.scrollLeft >= max - 2) { probe = Infinity; }
        var enter = clamp((vh * 0.92 - r.top) / (vh * 0.55), 0, 1);
        target = (Math.min(probe, start + len) - start) * enter;
        if (prev) { prev.disabled = vp.scrollLeft <= 2; }
        if (next) { next.disabled = vp.scrollLeft >= max - 2; }
      } else {
        var p = clamp((vh * 0.85 - r.top) / Math.max(1, r.height * 0.9), 0, 1);
        target = p * len;
      }
      var fill = clamp(target, 0, len);
      inner.style.setProperty('--fill', fill.toFixed(1) + 'px');
      var cur = 0;
      pos.forEach(function (x, i) {
        var lit = x - start <= fill + 1;
        steps[i].classList.toggle('is-lit', lit);
        if (lit) { cur = i; }
      });
      steps.forEach(function (s, i) { s.classList.toggle('is-current', i === cur); });
      if (curEl) { curEl.textContent = (cur < 9 ? '0' : '') + (cur + 1); }
      if (mini) { el.style.setProperty('--pf-p', len ? (fill / len).toFixed(3) : '1'); }
    }
    var schedule = rafThrottle(update);

    function stepBy(dir) {
      var w = steps[0].offsetWidth;
      var gap = parseFloat(window.getComputedStyle(steps[0].parentNode).columnGap || window.getComputedStyle(steps[0].parentNode).gap) || 18;
      vp.scrollBy({ left: dir * (w + gap), behavior: behavior() });
    }
    if (prev) { offs.push(on(prev, 'click', function () { stepBy(-1); })); }
    if (next) { offs.push(on(next, 'click', function () { stepBy(1); })); }

    /* drag to scroll with a mouse */
    var down = false, dragging = false, sx = 0, sl = 0;
    function snapNearest() {
      var best = 0, bestD = Infinity;
      var padL = parseFloat(window.getComputedStyle(vp).scrollPaddingLeft) || inner.offsetLeft;
      steps.forEach(function (s) {
        var x = inner.offsetLeft + s.offsetLeft - padL;
        var d = Math.abs(x - vp.scrollLeft);
        if (d < bestD) { bestD = d; best = x; }
      });
      vp.scrollTo({ left: best, behavior: behavior() });
    }
    offs.push(on(vp, 'pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || vertical || !overflow) { return; }
      down = true; dragging = false; sx = e.clientX; sl = vp.scrollLeft;
    }));
    offs.push(on(window, 'pointermove', function (e) {
      if (!down) { return; }
      var dx = e.clientX - sx;
      if (!dragging && Math.abs(dx) > 6) { dragging = true; vp.classList.add('is-dragging'); }
      if (dragging) { vp.scrollLeft = sl - dx; }
    }));
    offs.push(on(window, 'pointerup', function () {
      if (!down) { return; }
      down = false;
      if (dragging) {
        vp.classList.remove('is-dragging');
        var stop = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
        vp.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(function () { vp.removeEventListener('click', stop, { capture: true }); }, 0);
        snapNearest();
      }
      dragging = false;
    }));

    /* only listen to scroll while the section is near the viewport */
    var offScroll = null;
    function listen(onOff) {
      if (onOff && !offScroll) {
        var a = on(window, 'scroll', schedule, { passive: true });
        var b = on(vp, 'scroll', schedule, { passive: true });
        offScroll = function () { a(); b(); };
        schedule();
      } else if (!onOff && offScroll) { offScroll(); offScroll = null; }
    }
    if (hasIO) {
      var near = new IntersectionObserver(function (entries) { active = entries[0].isIntersecting; listen(active); }, { rootMargin: '200px 0px' });
      near.observe(el);
      observers.push(near);
      if (!reduced()) {
        el.classList.add('is-ready');
        var rev = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('is-in'); rev.unobserve(en.target); } });
        }, { threshold: 0.12 });
        steps.forEach(function (s) { rev.observe(s); });
        observers.push(rev);
      }
    } else {
      listen(true);
    }
    offs.push(on(window, 'resize', rafThrottle(measure)));
    if (mqV.addEventListener) { mqV.addEventListener('change', measure); offs.push(function () { mqV.removeEventListener('change', measure); }); }
    if ('ResizeObserver' in window) { ro = new ResizeObserver(rafThrottle(measure)); ro.observe(inner); }
    if (doc.fonts && doc.fonts.ready) { doc.fonts.ready.then(measure); }
    measure();

    return {
      el: el,
      select: function (blockEl) {
        var i = steps.indexOf(blockEl);
        if (i < 0) { return; }
        steps[i].classList.add('is-in');
        if (!vertical && overflow) { vp.scrollTo({ left: steps[i].offsetLeft, behavior: behavior() }); }
      },
      destroy: function () {
        offs.forEach(function (f) { f(); });
        if (offScroll) { offScroll(); }
        observers.forEach(function (o) { o.disconnect(); });
        if (ro) { ro.disconnect(); }
      }
    };
  }

  /* ================================================================ FILM EXPLORER */
  var XICON = '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  function filmExplorer(el) {
    var cards = $$('[data-fe-card]', el);
    if (!cards.length) { return null; }
    var max = parseInt(el.getAttribute('data-max'), 10) || 3;
    var filters = $$('[data-fe-filter]', el);
    var status = el.querySelector('[data-fe-status]');
    var empty = el.querySelector('[data-fe-empty]');
    var tray = el.querySelector('[data-fe-tray]');
    var trayList = el.querySelector('[data-fe-tray-list]');
    var countEl = el.querySelector('[data-fe-count]');
    var openBtn = el.querySelector('[data-fe-open]');
    var clearBtn = el.querySelector('[data-fe-clear]');
    var dialog = el.querySelector('[data-fe-dialog]');
    var tableWrap = el.querySelector('[data-fe-table]');
    var closeBtn = el.querySelector('[data-fe-close]');
    var boxes = cards.map(function (c) { return c.querySelector('[data-fe-compare]'); });
    var picked = [];
    var offs = [];
    var lastFocus = null;
    var familyLabel = 'All';

    function txt(card, key) {
      var n = card.querySelector('[data-fe-k="' + key + '"]');
      return n ? n.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    function say(msg) { if (status) { status.textContent = msg; } }
    function visibleCount() { return cards.filter(function (c) { return !c.hidden; }).length; }
    function baseStatus() {
      var n = visibleCount();
      return 'Showing ' + n + (n === 1 ? ' film' : ' films') + (familyLabel !== 'All' ? ' in ' + familyLabel : '') + '.';
    }

    function setFilter(key, btn) {
      filters.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
      familyLabel = key === 'all' ? 'All' : (btn.childNodes[0] ? btn.childNodes[0].textContent.trim() : key);
      var k = 0;
      cards.forEach(function (c) {
        var show = key === 'all' || c.getAttribute('data-family') === key;
        var was = !c.hidden;
        c.hidden = !show;
        if (show && !was && !reduced()) {
          c.style.setProperty('--k', k++);
          c.classList.remove('is-entering'); void c.offsetWidth; c.classList.add('is-entering');
        }
      });
      if (empty) { empty.hidden = visibleCount() > 0; }
      say(baseStatus());
    }
    filters.forEach(function (b) {
      offs.push(on(b, 'click', function () { setFilter(b.getAttribute('data-fe-filter'), b); }));
    });

    function renderTray() {
      var n = picked.length;
      if (tray) { tray.hidden = n === 0; }
      if (countEl) { countEl.textContent = String(n); }
      if (openBtn) { openBtn.disabled = n < 2; openBtn.setAttribute('aria-label', (openBtn.textContent.trim() || 'Compare') + ', ' + n + ' selected' + (n < 2 ? '. Pick at least 2.' : '')); }
      cards.forEach(function (c, i) { c.classList.toggle('is-picked', picked.indexOf(i) > -1); });
      boxes.forEach(function (b, i) { if (b) { b.setAttribute('aria-disabled', String(n >= max && picked.indexOf(i) < 0)); } });
      if (!trayList) { return; }
      trayList.innerHTML = '';
      picked.forEach(function (i) {
        var c = cards[i];
        var li = doc.createElement('li');
        li.className = 'fe-tray-chip';
        var dot = doc.createElement('span');
        dot.className = 'fe-dot cwv-fx fx-' + (c.getAttribute('data-finish') || 'gloss');
        dot.setAttribute('aria-hidden', 'true');
        var name = doc.createElement('span');
        name.textContent = txt(c, 'name');
        var x = doc.createElement('button');
        x.type = 'button';
        x.className = 'fe-tray-x';
        x.setAttribute('aria-label', 'Remove ' + txt(c, 'name') + ' from comparison');
        x.innerHTML = XICON;
        x.addEventListener('click', function () {
          if (boxes[i]) { boxes[i].checked = false; }
          toggle(i, false);
          var nextFocus = trayList.querySelector('.fe-tray-x') || (boxes[i] || null);
          if (nextFocus && nextFocus !== x) { nextFocus.focus(); } else if (boxes[i]) { boxes[i].focus(); }
        });
        li.appendChild(dot); li.appendChild(name); li.appendChild(x);
        trayList.appendChild(li);
      });
    }
    function toggle(i, onOff) {
      var at = picked.indexOf(i);
      if (onOff && at < 0) {
        if (picked.length >= max) {
          if (boxes[i]) { boxes[i].checked = false; }
          say('You can compare up to ' + max + ' films. Remove one to add another.');
          return;
        }
        picked.push(i);
        say(txt(cards[i], 'name') + ' added. ' + picked.length + ' of ' + max + ' selected.');
      } else if (!onOff && at > -1) {
        picked.splice(at, 1);
        say(txt(cards[i], 'name') + ' removed. ' + picked.length + ' of ' + max + ' selected.');
      }
      renderTray();
    }
    boxes.forEach(function (b, i) {
      if (!b) { return; }
      offs.push(on(b, 'change', function () { toggle(i, b.checked); }));
    });
    if (clearBtn) {
      offs.push(on(clearBtn, 'click', function () {
        picked.slice().forEach(function (i) { if (boxes[i]) { boxes[i].checked = false; } });
        picked = [];
        renderTray();
        say('Comparison cleared. ' + baseStatus());
      }));
    }

    function buildTable() {
      var rows = [
        { k: 'finish', label: 'Finish' }, { k: 'family', label: 'Family' }, { k: 'thickness', label: 'Thickness' },
        { k: 'durability', label: 'Durability' }, { k: 'best_for', label: 'Best for' }, { k: 'not_for', label: 'Not for' },
        { k: 'price_tier', label: 'Price tier' }, { k: 'brands', label: 'Brands' }
      ];
      var films = picked.map(function (i) { return cards[i]; });
      var t = doc.createElement('table');
      t.className = 'fe-table';
      var cap = doc.createElement('caption');
      cap.className = 'cwv-sr-only';
      cap.textContent = 'Comparison of ' + films.map(function (c) { return txt(c, 'name'); }).join(', ');
      t.appendChild(cap);
      var thead = doc.createElement('thead');
      var hr = doc.createElement('tr');
      var th0 = doc.createElement('th');
      th0.scope = 'col';
      th0.innerHTML = '<span class="cwv-sr-only">Spec</span>';
      hr.appendChild(th0);
      films.forEach(function (c) {
        var th = doc.createElement('th');
        th.scope = 'col';
        var wrap = doc.createElement('div');
        wrap.className = 'fe-th-film';
        var sw = doc.createElement('span');
        sw.className = 'fe-th-sw cwv-fx fx-' + (c.getAttribute('data-finish') || 'gloss');
        sw.setAttribute('aria-hidden', 'true');
        var nm = doc.createElement('span');
        nm.className = 'fe-th-name';
        nm.textContent = txt(c, 'name');
        wrap.appendChild(sw); wrap.appendChild(nm);
        th.appendChild(wrap);
        hr.appendChild(th);
      });
      thead.appendChild(hr);
      t.appendChild(thead);
      var tb = doc.createElement('tbody');
      rows.forEach(function (r) {
        var vals = films.map(function (c) {
          if (r.k === 'price_tier') {
            var dd = c.querySelector('[data-fe-k="price_tier"]');
            var v = dd ? dd.getAttribute('data-fe-v') : '';
            return v === 'na' ? 'Not rated' : (v ? v + ' (' + v.length + ' of 4)' : '');
          }
          return txt(c, r.k);
        });
        if (vals.every(function (v) { return !v; })) { return; }
        var tr = doc.createElement('tr');
        var uniq = vals.filter(function (v, i, a) { return a.indexOf(v) === i; });
        if (uniq.length > 1) { tr.className = 'is-diff'; }
        var th = doc.createElement('th');
        th.scope = 'row';
        th.textContent = r.label;
        tr.appendChild(th);
        vals.forEach(function (v) {
          var td = doc.createElement('td');
          td.textContent = v || '–';
          tr.appendChild(td);
        });
        tb.appendChild(tr);
      });
      t.appendChild(tb);
      tableWrap.innerHTML = '';
      tableWrap.appendChild(t);
    }

    var fallbackKey = null;
    function openDialog() {
      if (!dialog || picked.length < 2) { return; }
      buildTable();
      lastFocus = doc.activeElement;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('is-fallback');
        fallbackKey = on(doc, 'keydown', function (e) { if (e.key === 'Escape') { closeDialog(); } });
      }
      if (closeBtn) { closeBtn.focus(); }
    }
    function closeDialog() {
      if (!dialog) { return; }
      if (typeof dialog.close === 'function' && dialog.open) { dialog.close(); }
      else { dialog.removeAttribute('open'); dialog.classList.remove('is-fallback'); afterClose(); }
      if (fallbackKey) { fallbackKey(); fallbackKey = null; }
    }
    function afterClose() {
      var f = lastFocus && doc.contains(lastFocus) ? lastFocus : openBtn;
      if (f && f.focus) { f.focus(); }
    }
    if (openBtn) { offs.push(on(openBtn, 'click', openDialog)); }
    if (closeBtn) { offs.push(on(closeBtn, 'click', closeDialog)); }
    if (dialog) {
      offs.push(on(dialog, 'close', afterClose));
      offs.push(on(dialog, 'click', function (e) { if (e.target === dialog) { closeDialog(); } }));
    }

    renderTray();
    el.classList.add('is-ready');
    return {
      el: el,
      select: function (blockEl) {
        if (blockEl && blockEl.hidden) {
          var all = filters.filter(function (b) { return b.getAttribute('data-fe-filter') === 'all'; })[0];
          if (all) { setFilter('all', all); }
        }
        if (blockEl && blockEl.scrollIntoView) { blockEl.scrollIntoView({ block: 'center', behavior: behavior() }); }
      },
      destroy: function () {
        offs.forEach(function (f) { f(); });
        if (fallbackKey) { fallbackKey(); }
        if (dialog && dialog.open && typeof dialog.close === 'function') { dialog.close(); }
      }
    };
  }

  /* ================================================================ TIMELINE */
  function timeline(el) {
    var body = el.querySelector('[data-tl-body]');
    var eras = $$('[data-tl-era]', el);
    if (!body || !eras.length) { return null; }
    var scrub = el.querySelector('[data-tl-scrub]');
    var list = el.querySelector('[data-tl-scrub-list]');
    var offs = [], observers = [];
    var nodeY = [], height = 0, offScroll = null;
    var decades = [], eraDecade = [];

    eras.forEach(function (era) {
      var y = era.getAttribute('data-year') || '';
      var m = /(\d{4})/.exec(y);
      var key = null, label = null;
      if (m) { key = Math.floor(parseInt(m[1], 10) / 10) * 10; label = key + 's'; }
      else if (/today|now|present/i.test(y)) { key = 99999; label = 'Today'; }
      eraDecade.push(key);
      if (key !== null && !decades.some(function (d) { return d.key === key; })) {
        decades.push({ key: key, label: label, era: era });
      }
    });
    decades.sort(function (a, b) { return a.key - b.key; });
    var btns = [];
    if (scrub && list && decades.length >= 3) {
      decades.forEach(function (d) {
        var li = doc.createElement('li');
        var b = doc.createElement('button');
        b.type = 'button';
        b.className = 'tl-scrub-btn';
        b.textContent = d.label;
        b.setAttribute('aria-label', 'Jump to the ' + d.label);
        b.addEventListener('click', function () {
          d.era.classList.add('is-in');
          var y = d.era.getBoundingClientRect().top + (window.pageYOffset || html.scrollTop) - (window.innerHeight || 800) * 0.28;
          window.scrollTo({ top: Math.max(0, y), behavior: behavior() });
          try { d.era.focus({ preventScroll: true }); } catch (e) { d.era.focus(); }
        });
        li.appendChild(b);
        list.appendChild(li);
        btns.push({ key: d.key, btn: b });
      });
      scrub.hidden = false;
    }

    function measure() {
      height = body.offsetHeight;
      nodeY = eras.map(function (era) {
        var n = era.querySelector('.tl-node') || era;
        return era.offsetTop + n.offsetTop + n.offsetHeight / 2;
      });
      update();
    }
    function update() {
      var r = body.getBoundingClientRect();
      var probe = (window.innerHeight || 800) * 0.58;
      var fill = clamp(probe - r.top, 0, height);
      body.style.setProperty('--tl-fill', fill.toFixed(1) + 'px');
      var cur = -1;
      nodeY.forEach(function (y, i) {
        var lit = y <= fill;
        eras[i].classList.toggle('is-lit', lit);
        if (lit) { cur = i; }
      });
      if (scrub) { scrub.style.setProperty('--tl-p', height ? (fill / height).toFixed(3) : '0'); }
      if (btns.length) {
        var key = cur > -1 ? eraDecade[cur] : null;
        if (key === null && cur > -1) { for (var j = cur; j >= 0; j--) { if (eraDecade[j] !== null) { key = eraDecade[j]; break; } } }
        btns.forEach(function (b) {
          var is = b.key === key;
          if (is) { b.btn.setAttribute('aria-current', 'true'); } else { b.btn.removeAttribute('aria-current'); }
          if (is && b.btn.parentNode && b.btn.parentNode.parentNode) {
            var l = b.btn.parentNode.parentNode;
            var bl = b.btn.offsetLeft, bw = b.btn.offsetWidth;
            if (bl < l.scrollLeft || bl + bw > l.scrollLeft + l.clientWidth) { l.scrollLeft = bl - l.clientWidth / 2 + bw / 2; }
          }
        });
      }
    }
    var schedule = rafThrottle(update);
    function listen(onOff) {
      if (onOff && !offScroll) { offScroll = on(window, 'scroll', schedule, { passive: true }); schedule(); }
      else if (!onOff && offScroll) { offScroll(); offScroll = null; }
    }

    if (hasIO) {
      var near = new IntersectionObserver(function (en) { listen(en[0].isIntersecting); }, { rootMargin: '300px 0px' });
      near.observe(el);
      observers.push(near);
      if (!reduced()) {
        var vh = window.innerHeight || 800;
        eras.forEach(function (era) {
          var rr = era.getBoundingClientRect();
          if (rr.top < vh && rr.bottom > 0) { era.classList.add('is-in'); }
        });
        el.classList.add('is-ready');
        var rev = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('is-in'); rev.unobserve(en.target); } });
        }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
        eras.forEach(function (era) { if (!era.classList.contains('is-in')) { rev.observe(era); } });
        observers.push(rev);
      }
    } else {
      listen(true);
    }
    offs.push(on(window, 'resize', rafThrottle(measure)));
    var ro = null;
    if ('ResizeObserver' in window) { ro = new ResizeObserver(rafThrottle(measure)); ro.observe(body); }
    if (doc.fonts && doc.fonts.ready) { doc.fonts.ready.then(measure); }
    measure();

    return {
      el: el,
      select: function (blockEl) {
        if (eras.indexOf(blockEl) > -1) { blockEl.classList.add('is-in'); blockEl.scrollIntoView({ block: 'center', behavior: behavior() }); }
      },
      destroy: function () {
        offs.forEach(function (f) { f(); });
        if (offScroll) { offScroll(); }
        observers.forEach(function (o) { o.disconnect(); });
        if (ro) { ro.disconnect(); }
      }
    };
  }

  /* ================================================================ GLOSSARY */
  function glossary(el) {
    var input = el.querySelector('[data-gl-q]');
    var termsEls = $$('[data-gl-term]', el);
    if (!termsEls.length) { return null; }
    var clearBtn = el.querySelector('[data-gl-clear]');
    var status = el.querySelector('[data-gl-status]');
    var countEl = el.querySelector('[data-gl-count]');
    var empty = el.querySelector('[data-gl-empty]');
    var resetBtn = el.querySelector('[data-gl-reset]');
    var groups = $$('[data-gl-group]', el);
    var letters = $$('[data-gl-letter]', el);
    var total = termsEls.length;
    var offs = [], observers = [];
    var timer = null;

    var terms = termsEls.map(function (t) {
      var nameEl = t.querySelector('[data-gl-name]');
      var name = nameEl ? nameEl.textContent : '';
      return { el: t, nameEl: nameEl, name: name, nName: norm(name), hay: norm(t.textContent) };
    });

    function highlight(term, words) {
      if (!term.nameEl) { return; }
      var n = term.name;
      if (!words.length) { term.nameEl.textContent = n; return; }
      var lower = norm(n);
      var marks = [];
      words.forEach(function (w) {
        var at = lower.indexOf(w);
        if (at > -1 && lower.length === n.length) { marks.push([at, at + w.length]); }
      });
      if (!marks.length) { term.nameEl.textContent = n; return; }
      marks.sort(function (a, b) { return a[0] - b[0]; });
      var frag = doc.createDocumentFragment(), pos = 0;
      marks.forEach(function (m) {
        if (m[0] < pos) { return; }
        if (m[0] > pos) { frag.appendChild(doc.createTextNode(n.slice(pos, m[0]))); }
        var mk = doc.createElement('mark');
        mk.textContent = n.slice(m[0], m[1]);
        frag.appendChild(mk);
        pos = m[1];
      });
      if (pos < n.length) { frag.appendChild(doc.createTextNode(n.slice(pos))); }
      term.nameEl.textContent = '';
      term.nameEl.appendChild(frag);
    }

    function apply() {
      var raw = input ? input.value : '';
      var q = norm(raw);
      var words = q ? q.split(' ').filter(Boolean) : [];
      var shown = 0;
      terms.forEach(function (t) {
        var ok = words.every(function (w) { return t.hay.indexOf(w) > -1; });
        t.el.hidden = !ok;
        if (ok) { shown++; }
        highlight(t, ok ? words : []);
      });
      var visibleGroups = {};
      groups.forEach(function (g) {
        var any = $$('[data-gl-term]', g).some(function (t) { return !t.hidden; });
        g.hidden = !any;
        visibleGroups[g.getAttribute('data-gl-group')] = any;
      });
      letters.forEach(function (a) {
        var ok = visibleGroups[a.getAttribute('data-gl-letter')];
        a.classList.toggle('is-off', !ok);
        if (ok) { a.removeAttribute('aria-disabled'); a.removeAttribute('tabindex'); }
        else { a.setAttribute('aria-disabled', 'true'); a.setAttribute('tabindex', '-1'); }
      });
      if (clearBtn) { clearBtn.hidden = !raw; }
      if (empty) { empty.hidden = shown > 0; }
      if (status) {
        status.textContent = words.length
          ? shown + ' of ' + total + ' terms match “' + raw.trim() + '”'
          : total + (total === 1 ? ' term' : ' terms');
      }
      if (countEl) { countEl.textContent = words.length ? shown + ' / ' + total : total + (total === 1 ? ' term' : ' terms'); }
    }
    function reset(focus) {
      if (input) { input.value = ''; }
      apply();
      if (focus && input) { input.focus(); }
    }
    if (input) {
      offs.push(on(input, 'input', function () { clearTimeout(timer); timer = setTimeout(apply, 90); }));
      offs.push(on(input, 'keydown', function (e) {
        if (e.key === 'Escape' && input.value) { e.preventDefault(); reset(true); }
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(timer); apply(); }
      }));
    }
    if (clearBtn) { offs.push(on(clearBtn, 'click', function () { reset(true); })); }
    if (resetBtn) { offs.push(on(resetBtn, 'click', function () { reset(true); })); }

    function flash(t) {
      $$('.gl-term.is-target', el).forEach(function (x) { x.classList.remove('is-target'); });
      void t.offsetWidth;
      t.classList.add('is-target');
    }
    function goTo(id, push) {
      var t = doc.getElementById(id);
      if (!t || !el.contains(t)) { return false; }
      if (t.hidden || (t.closest('[data-gl-group]') || {}).hidden) { reset(false); }
      t.scrollIntoView({ block: 'start', behavior: behavior() });
      if (push && window.history && window.history.pushState) { window.history.pushState(null, '', '#' + id); }
      var focusEl = t.querySelector('[data-gl-name]') || t;
      try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); }
      flash(t);
      return true;
    }
    offs.push(on(el, 'click', function (e) {
      var a = e.target.closest ? e.target.closest('a[data-gl-rel], a[data-gl-name]') : null;
      if (!a) { return; }
      var id = (a.getAttribute('href') || '').replace(/^#/, '');
      if (id && goTo(id, true)) { e.preventDefault(); }
    }));
    offs.push(on(el, 'click', function (e) {
      var a = e.target.closest ? e.target.closest('a[data-gl-letter]') : null;
      if (a && a.classList.contains('is-off')) { e.preventDefault(); }
    }));

    /* current letter while scrolling */
    if (hasIO && letters.length) {
      var cur = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) { return; }
          var key = en.target.getAttribute('data-gl-group');
          groups.forEach(function (g) { g.classList.toggle('is-current', g === en.target); });
          letters.forEach(function (a) { a.classList.toggle('is-current', a.getAttribute('data-gl-letter') === key); });
        });
      }, { rootMargin: '-35% 0px -60% 0px' });
      groups.forEach(function (g) { cur.observe(g); });
      observers.push(cur);
    }

    if (window.location.hash) {
      var id = decodeURIComponent(window.location.hash.slice(1));
      var t = id ? doc.getElementById(id) : null;
      if (t && el.contains(t) && t.hasAttribute('data-gl-term')) { flash(t); }
    }
    el.classList.add('is-ready');
    return {
      el: el,
      select: function (blockEl) { if (blockEl && blockEl.hidden) { reset(false); } if (blockEl) { blockEl.scrollIntoView({ block: 'center', behavior: behavior() }); } },
      destroy: function () {
        clearTimeout(timer);
        offs.forEach(function (f) { f(); });
        observers.forEach(function (o) { o.disconnect(); });
      }
    };
  }

  /* ================================================================ registry */
  var TYPES = [
    { sel: '[data-cwv-ps]', fn: printStack },
    { sel: '[data-cwv-pf]', fn: processFlow },
    { sel: '[data-cwv-fe]', fn: filmExplorer },
    { sel: '[data-cwv-tl]', fn: timeline },
    { sel: '[data-cwv-gl]', fn: glossary }
  ];
  var instances = [];

  function initIn(scope) {
    TYPES.forEach(function (t) {
      var els = scope.matches && scope.matches(t.sel) ? [scope] : [];
      els = els.concat($$(t.sel, scope));
      els.forEach(function (el) {
        if (el.__cwvEdu) { return; }
        try {
          var inst = t.fn(el);
          el.__cwvEdu = inst || true;
          if (inst) { instances.push(inst); }
        } catch (e) {
          if (window.console) { window.console.error('[CWV edu]', e); }
        }
      });
    });
  }
  function destroyIn(scope) {
    instances = instances.filter(function (inst) {
      if (scope.contains(inst.el)) {
        try { inst.destroy(); } catch (e) { /* ignore */ }
        inst.el.__cwvEdu = null;
        return false;
      }
      return true;
    });
  }

  CWV.edu = {
    __loaded: true,
    init: initIn,
    destroy: destroyIn,
    printStack: printStack,
    processFlow: processFlow,
    filmExplorer: filmExplorer,
    timeline: timeline,
    glossary: glossary
  };

  function boot() { initIn(doc); }
  if (doc.readyState === 'loading') { doc.addEventListener('DOMContentLoaded', boot); } else { boot(); }

  /* Shopify theme editor */
  doc.addEventListener('shopify:section:load', function (e) { initIn(e.target); });
  doc.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  doc.addEventListener('shopify:block:select', function (e) {
    instances.forEach(function (inst) {
      if (inst.select && inst.el.contains(e.target)) { inst.select(e.target); }
    });
  });
})();
